import React from 'react';
import styles from './_indirect-profit-calculator.module.scss';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import InfoIcon from "@mui/icons-material/Info";
import Divider from '@mui/material/Divider';
import { Grid, GridColumn as Column, GridCellProps } from '@progress/kendo-react-grid';
import { getAmortizationSchedule } from '../../utils/quoteUtils';
import { IQuote } from '../../Interfaces/Common/IQuote';
import {add, divide, multiply} from "@amsanalytics/react-shared-library";

export interface IIndirectProfitCalculatorData {
  netTradeAllowance: number;
  cashDownPayment: number;
  intoTotalCostPercent: string;
  purchasePrice: number;
  dealerFees: number;
  lenderFees: number;
  tradeInAllowance: number;
  amountToFinance: number;
  bookValue: number;
  monthlyDepreciationRate?: number | null;
  quote?: IQuote;
}
export interface IndirectProfitCalculatorProps {
  profitCalculatorData: IIndirectProfitCalculatorData;
}



export default function IndirectProfitCalculator(props: IndirectProfitCalculatorProps) {
  const { profitCalculatorData } = props;
  const formatValue = (value) => {
    const absValue = Math.abs(value);
    const formattedValue = absValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return value < 0 ? `(${formattedValue})` : formattedValue;
  };
  const topData = [
    { label: 'Net Trade Allowance', value: profitCalculatorData.netTradeAllowance, format: true },
    { label: 'Cash Down Payment', value: profitCalculatorData.cashDownPayment, format: true },
    { label: 'Into Total Cost', value: profitCalculatorData.intoTotalCostPercent, format: false },
    { label: 'Loan Value LTV:', value: profitCalculatorData.bookValue ? `${multiply(divide(profitCalculatorData.amountToFinance,  profitCalculatorData.bookValue), 100).toFixed(2)}%` : 'N/A', format: false },
  ];
  const bottomData = [
    { label: 'Purchase Price', value: profitCalculatorData.purchasePrice, format: true },
    { label: 'Dealer Fees', value: profitCalculatorData.dealerFees, format: true },
    { label: 'Lender Fees', value: profitCalculatorData.lenderFees, format: true },
    // { label: 'Lender Warranty', value: profitCalculatorData.lenderWarranty, format: true },
    { label: 'Less Down Pymt/Trade-In', value: -(add(profitCalculatorData.tradeInAllowance, profitCalculatorData.cashDownPayment)), format: true },
    { label: 'Loan Amount', value: profitCalculatorData.amountToFinance, format: true }
  ];

  const amortizationSchedule = props.profitCalculatorData.quote && props.profitCalculatorData.monthlyDepreciationRate ? getAmortizationSchedule(profitCalculatorData.quote!, profitCalculatorData.monthlyDepreciationRate, profitCalculatorData.bookValue) : null;
  const breakevenPaymentNo = amortizationSchedule && amortizationSchedule.findIndex(as => as.endingPrincipal < as.collateralValue!) + 1;
  
  return (
    <div className={styles.indirectProfitCalculatorContainer}>
      <Card>
        <CardHeader
          avatar={<InfoIcon fontSize="small" />}
          title="Profit Calculator"
        />
        <Divider variant="middle" />
        <div className={styles.profitCalculator}>
          <div style={{ display: 'flex', flex: 1, flexDirection: 'column' }}>
            <Grid size='small' data={topData} className={styles.hideHeader}>
              <Column width='200px' field="label" title="Label" />
              <Column field="value" title="Value"
                cell={(cellProps: GridCellProps) => <td style={{ textAlign: 'right' }}>{cellProps.dataItem['format'] === true 
                  ? formatValue(cellProps.dataItem[cellProps.field!]) 
                  : cellProps.dataItem[cellProps.field!]}
                  </td>}
              />
            </Grid>

            <div className={styles.breakEvenContainer}>
              <div className={styles.breakEven}>
                <span>Breakeven payment No</span>
                <span>{breakevenPaymentNo ?? 'Unable to calculate'}</span>
              </div>
            </div>

            <Grid size='small' data={bottomData} className={styles.hideHeader}>
              <Column width='200px' field="label" title="Label" />
              <Column
                field="value"
                title="Value"
                cell={(cellProps: GridCellProps) => <td style={{ textAlign: 'right' }}>{formatValue(cellProps.dataItem[cellProps.field!])}</td>}
              />
            </Grid>
          </div>

        </div>
      </Card>
    </div>
  );
}
